'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { isEmpty, omit } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'

import { Card } from '@/entities/auth'
import {
  THostInfoForm,
  useSignUpHostStore,
  createZHostInfoForm,
} from '@/features/sign-up'
import {
  EmailField,
  PasswordField,
  NameField,
  PasswordConfirmField,
  ProfileImageField,
  HostIntroField,
} from '@/features/update-user-information'
import {
  PhoneVerificationResult,
  useValidatePhoneNumber,
} from '@/features/verify-identity'
import { useRouter } from '@/shared/i18n'
import { Slot, useDebounce } from '@/shared/lib'
import { Button, Form } from '@/shared/ui'
import { useValidateEmail } from '@/widgets/sign-up'

import { PhoneVerificationField } from './phone-verification-field'

export const SignUpHostInfo = () => {
  const router = useRouter()

  const t = useTranslations('user_information')
  const tAccount = useTranslations('account')

  const { actions, info } = useSignUpHostStore()

  const { validateEmailAsync } = useValidateEmail()
  const { validatePhoneNumberAsync } = useValidatePhoneNumber()

  const defaultValues = { ...info, certNum: '', phoneNumber: '' }

  const form = useForm<THostInfoForm>({
    resolver: zodResolver(createZHostInfoForm(tAccount)),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const watch = useWatch({
    control: form.control,
  })
  const debouncedWatch = useDebounce(watch, 300)

  const handleSuccessPhoneVerification = ({
    certNum,
    phoneNumber,
  }: PhoneVerificationResult) => {
    form.setValue('phoneNumber', phoneNumber)
    form.setValue('certNum', certNum)
    toast(t('toast.verification_completed'))
  }

  const validateDuplicateUser = async (formValues: THostInfoForm) => {
    const isValidEmail = await validateEmailAsync(formValues.email)

    if (!isValidEmail) return false

    const isValidPhoneNumber = await validatePhoneNumberAsync(
      formValues.phoneNumber,
    )

    if (!isValidPhoneNumber) return false

    return true
  }

  const submitForm = async (formValues: THostInfoForm) => {
    if (isEmpty(formValues.phoneNumber)) {
      toast(t('toast.verification_required'))

      return
    }

    const isValidDuplicateUser = await validateDuplicateUser(formValues)

    if (!isValidDuplicateUser) {
      return
    }

    actions.updateInfo(formValues)

    router.push('/sign-up/host?step=pay')
  }

  useEffect(() => {
    const infoWithoutVerification = omit(debouncedWatch, [
      'certNum',
      'phoneNumber',
    ])

    actions.updateInfo(infoWithoutVerification as THostInfoForm)
  }, [debouncedWatch, actions])

  useEffect(() => {
    actions.updateInfo(defaultValues)
  }, [])

  return (
    <Card progress={50}>
      <Slot name="title">{t('title')}</Slot>
      <Slot name="children">
        <Form.Root form={form} className="space-y-6">
          <EmailField />
          <NameField />
          <PasswordField />
          <PasswordConfirmField />
          <PhoneVerificationField onSuccess={handleSuccessPhoneVerification} />
          <ProfileImageField />
          <HostIntroField />

          <Button
            size="large"
            className="mt-10"
            type="submit"
            fullWidth
            onClick={form.handleSubmit(submitForm)}
          >
            {t('button.next')}
          </Button>
        </Form.Root>
      </Slot>
    </Card>
  )
}
